<template>
  <!-- 레이어(절대좌표)형 팝업만 렌더링 (window타입은 window.open으로 처리) -->
  <div v-if="variant === 'layer' && open" class="lypop" :style="styleLayer">
    <!-- 헤더 (드래그 가능) -->
    <div class="header" @mousedown="startDrag">
      <span class="header-title">{{ title }}</span>
      <button type="button" class="btn-close-x" title="닫기" @click.prevent="close">
        <i class="ri-close-line"></i>
      </button>
    </div>
    <!-- 콘텐츠 -->
    <div class="contents" v-dompurify-html="html"></div>
    <!-- 하단 바 -->
    <div class="btm">
      <template v-if="stopVewAt">
        <label style="color:#000;">
          <input type="checkbox" @change="onToggleToday" />
          오늘하루 열지않음
        </label>
      </template>
      <a href="#" class="btn_close" @click.prevent="close">창닫기</a>
    </div>
  </div>
</template>
<script setup>
import { computed, watch, ref, onBeforeUnmount } from 'vue'
import { getCookie, setCookie } from '@/utils/cookie'
import '@/assets/css/popup.css'

const props = defineProps({
  modelValue: { type: Boolean, default: true },      // v-model 제어
  popup: { type: Object, required: true },           // { popupId, variant, contentHtml, title, left, top, width, height, zIndex, stopVewAt, expireDays, visible }
})

const emit = defineEmits(['update:modelValue', 'close'])

const id = computed(() => props.popup?.popupId || '')
const variant = computed(() => {
  const v = props.popup?.variant || 'layer'
  return v
})
const title = computed(() => props.popup?.title || '')
const html = computed(() => props.popup?.contentHtml || '')
const stopVewAt = computed(() => props.popup?.stopVewAt === 'Y')
const expireDays = computed(() => Number(props.popup?.expireDays || 1))

// 드래그 상태
const isDragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })
const currentPos = ref({ left: 0, top: 0 })

// 초기 위치 설정
watch(() => props.popup, (popup) => {
  if (popup) {
    currentPos.value = {
      left: popup.left ?? 50,
      top: popup.top ?? 50
    }
  }
}, { immediate: true })

const styleLayer = computed(() => ({
  left: currentPos.value.left + 'px',
  top: currentPos.value.top + 'px',
  width: (props.popup?.width ?? 400) + 'px',
  height: (props.popup?.height ?? 500) + 'px',
  zIndex: props.popup?.zIndex ?? 99999,
}))

function isBlockedToday() {
  if (!stopVewAt.value || !id.value) return false
  return getCookie(`POP_STOP_${id.value}`) === '1'
}

// open을 computed로 관리 (layer 팝업만 처리)
const open = computed({
  get() {
    return props.modelValue && !isBlockedToday()
  },
  set(v) {
    emit('update:modelValue', v)
  }
})

// 드래그 시작
function startDrag(e) {
  if (e.button !== 0) return // 좌클릭만
  e.preventDefault() // 브라우저 기본 drag 동작 방지
  isDragging.value = true
  
  // currentPos 기준으로 offset 계산 (getBoundingClientRect와 currentPos 불일치 방지)
  dragOffset.value = {
    x: e.clientX - currentPos.value.left,
    y: e.clientY - currentPos.value.top
  }
  
  document.addEventListener('mousemove', onDragMove)
  document.addEventListener('mouseup', onDragEnd)
}

// 드래그 중
function onDragMove(e) {
  if (!isDragging.value) return
  e.preventDefault() // 텍스트 선택 방지 (순간이동 방지)
  
  currentPos.value = {
    left: e.clientX - dragOffset.value.x,
    top: e.clientY - dragOffset.value.y
  }
}

// 드래그 종료
function onDragEnd() {
  isDragging.value = false
  document.removeEventListener('mousemove', onDragMove)
  document.removeEventListener('mouseup', onDragEnd)
}

// 정리
onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onDragMove)
  document.removeEventListener('mouseup', onDragEnd)
})

function close() {
  open.value = false
  emit('close')
}

function onToggleToday(e) {
  if (!stopVewAt.value || !id.value) return
  const checked = e?.target?.checked
  if (checked) {
    const days = Math.max(1, expireDays.value|0)
    setCookie(`POP_STOP_${id.value}`, '1', { days })
  } else {
    setCookie(`POP_STOP_${id.value}`, '', { days: -1 }) // 삭제
  }
}
</script>
<style scoped>
.lypop {
  position: fixed;
  display: flex;
  flex-direction: column;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  z-index: 99999 !important;
  /* popup.css의 .lypop { left:50%; margin-left:-207px } 오버라이드 */
  margin-left: 0 !important;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: move;
  user-select: none;
  flex-shrink: 0;
  height: auto !important;
  background: #f5f5f5;
  color: #000;
}

.header-title {
  font-weight: 600;
  font-size: 15px;
  color: #000;
  letter-spacing: -0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-close-x {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.2);
  color: #000;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}

.btn-close-x:hover {
  background: rgba(255, 255, 255, 0.35);
}

.contents {
  padding: 16px;
  overflow: auto;
  flex: 1;
}

.btm {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px !important;
  background: #f9fafb !important;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btm label {
  font-size: 13px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.btm .btn_close {
  margin-left: auto;
  font-size: 13px;
  color: #6b7280;
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff !important;
  transition: all 0.2s;
}

.btm .btn_close:hover {
  background: #f3f4f6;
  color: #111827;
}
</style>
